import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';

/**
 * Messages des echecs de la connexion Google, dont le callback revient
 * sur `/connexion?erreur=...`. Le code est volontairement pauvre : le
 * detail (adresse, cause) vit dans le journal d'audit, pas dans une URL.
 */
const ERREURS_GOOGLE: Record<string, string> = {
  google_inconnu: 'Aucun compte n’est associe a cette adresse Google.',
  google: 'La connexion Google a echoue. Reessayez, ou utilisez votre mot de passe.',
  google_indisponible:
    'La connexion Google n’est pas configuree sur ce serveur. Utilisez votre mot de passe.',
};

export function ConnexionPage() {
  const { connecter, sessionExpiree } = useAuth();
  const [parametres] = useSearchParams();
  const [identifiant, setIdentifiant] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(
    () => ERREURS_GOOGLE[parametres.get('erreur') ?? ''] ?? null,
  );
  const [enCours, setEnCours] = useState(false);

  // Les voies ouvertes viennent du SERVEUR : afficher un formulaire qu'il
  // refusera est pire que ne pas l'afficher. Tant que la reponse n'est pas
  // la, on suppose les deux ouvertes — c'est le cas courant, et cela evite
  // un ecran qui se reorganise sous les yeux.
  const { donnees: voies } = useRessource(
    () => api.get<{ motDePasse: boolean; google: boolean }>('/api/auth/configuration'),
    [],
  );
  const motDePasseOuvert = voies?.motDePasse ?? true;
  const googleOuvert = voies?.google ?? true;

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      await connecter(identifiant, motDePasse);
    } catch (cause) {
      setErreur(
        cause instanceof ErreurApi ? cause.message : 'La connexion a echoue. Reessayez.',
      );
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-encre-100 p-4">
      <div className="carte w-full max-w-md p-8">
        <img src="/assets/logo-artistic.jpg" alt="Artistic" className="mx-auto mb-8 h-20 w-auto" />

        <h1 className="mb-1 text-center text-xl font-semibold">Gestion de l’ecole</h1>
        <p className="mb-6 text-center text-sm text-encre-500">
          Accedez a votre espace avec vos identifiants nominatifs.
        </p>

        {/* Arriver ici sans explication est deroutant : on croit avoir
            ete deconnecte par erreur. Le dire evite la question. */}
        {sessionExpiree && (
          <p
            className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
            role="status"
          >
            Votre session a expiré. Reconnectez-vous pour reprendre où vous en étiez.
          </p>
        )}

        {googleOuvert && (
          /* Navigation complete, pas un fetch : les redirections OAuth ne
             se suivent pas en XHR. */
          <a href="/api/auth/google/depart" className="bouton-secondaire flex w-full justify-center">
            Se connecter avec Google
          </a>
        )}

        {/* Le separateur n'a de sens qu'entre DEUX voies. */}
        {googleOuvert && motDePasseOuvert && (
          <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-wide text-encre-400">
            <span className="h-px flex-1 bg-encre-200" />
            ou
            <span className="h-px flex-1 bg-encre-200" />
          </div>
        )}

        {/* Le mot de passe est ferme : on le DIT, sans quoi la page parait
            amputee. L'erreur eventuelle reste affichee — c'est la seule
            place qui lui reste une fois le formulaire retire. */}
        {!motDePasseOuvert && (
          <>
            <p className="mt-5 rounded-md border border-encre-200 bg-encre-50 p-3 text-center text-xs text-encre-500">
              La connexion par mot de passe est desactivee sur ce serveur : l’acces se fait par
              votre compte Google d’ecole.
            </p>
            {erreur && (
              <p
                className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                role="alert"
              >
                {erreur}
              </p>
            )}
          </>
        )}

        {motDePasseOuvert && (
        <form onSubmit={soumettre} className="space-y-4">
          <label className="block">
            <span className="etiquette">Identifiant ou adresse email</span>
            <input
              className="champ"
              value={identifiant}
              onChange={(evenement) => setIdentifiant(evenement.target.value)}
              autoComplete="username"
              autoFocus
              required
            />
          </label>

          <label className="block">
            <span className="etiquette">Mot de passe</span>
            <input
              className="champ"
              type="password"
              value={motDePasse}
              onChange={(evenement) => setMotDePasse(evenement.target.value)}
              autoComplete="current-password"
              required
            />
          </label>

          {erreur && (
            <p
              className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
              role="alert"
            >
              {erreur}
            </p>
          )}

          <button type="submit" className="bouton-principal w-full" disabled={enCours}>
            {enCours ? 'Connexion…' : 'Se connecter'}
          </button>
        </form>
        )}

        <p className="mt-6 text-center text-xs text-encre-400">
          Les comptes sont crees par invitation. Il n’y a pas d’auto-inscription.
        </p>

      </div>
    </div>
  );
}
